// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import React from 'react';
import type { ProfileRequestDTO } from '../../types/profileRequest';
import type { Policy } from '../../types/policy';
import { RequestTypeBadge } from './RequestTypeBadge';
import { RequestDisplayStatusBadge, deriveDisplayStatus } from './RequestDisplayStatusBadge';
import { ownershipLabel, signatoryNamesFor, formatLastUpdated } from './requestCells';
// Reuse the Provider / Policy-Type / Policy-Number cell styling from the
// Policies table so this table reads identically to the Requests tab.
import '../PolicyList.css';
import './OutstandingRequestsTable.css';

export type NudgeMode = 'CLIENT' | 'PROVIDER';

/** Map a request's display status to the actor a nudge would target. */
function rowModeFor(request: ProfileRequestDTO): NudgeMode {
  return deriveDisplayStatus(request.status, request.actions) === 'PENDING_PROVIDER'
    ? 'PROVIDER'
    : 'CLIENT';
}

function ProviderChip({ name, logoUrl }: { name: string; logoUrl?: string | null }) {
  return (
    <div className="policy-provider">
      <div className="policy-provider__avatar">
        {logoUrl ? (
          <img src={logoUrl} alt={name} />
        ) : (
          name
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map((w) => w[0])
            .join('')
            .toUpperCase()
        )}
      </div>
      <span className="policy-provider__name">{name}</span>
    </div>
  );
}

export function ProviderCell({
  request,
  policyById,
}: {
  request: ProfileRequestDTO;
  policyById: Map<string, Policy>;
}) {
  // Policy-less typed requests (New Business) carry the provider on the request
  // itself; fall back to "Profile-only" only when there's genuinely nothing.
  if (!request.policyId) {
    return request.requestProviderName ? (
      <ProviderChip name={request.requestProviderName} />
    ) : (
      <span style={{ color: '#6b7280' }}>Profile-only</span>
    );
  }
  const policy = policyById.get(request.policyId);
  if (!policy) {
    return (
      <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>
        {request.policyId.slice(0, 8)}…
      </span>
    );
  }
  const providerName = policy.providerName || policy.carrier || 'Unknown';
  return <ProviderChip name={providerName} logoUrl={policy.providerLogoUrl} />;
}

export interface OutstandingRequestsTableProps {
  /** Rows to render. Callers pre-filter to the requests they want shown. */
  requests: ProfileRequestDTO[];
  policyById: Map<string, Policy>;
  /**
   * When set, rows whose nudge actor matches this mode are selectable
   * (highlighted) and the rest are shown dimmed + non-clickable. Omit for a
   * plain read-only table (the nudge email pages).
   */
  activeMode?: NudgeMode;
  /** Called when a selectable (active-mode) row is clicked. */
  onRowSelect?: (request: ProfileRequestDTO, rowMode: NudgeMode) => void;
  emptyMessage?: string;
  /** data-testid for the table wrapper. */
  testId?: string;
  /** Prefix for each row's data-testid (suffixed with the request id). */
  rowTestIdPrefix?: string;
}

/**
 * Presentational "outstanding requests" table — the same columns and styling
 * shared by the Requests tab, the "Choose a request" chooser, and the nudge
 * email pages: Provider · Policy Type · Policy No. · Type · Signatory · Last
 * updated · Request type · Status.
 *
 * <p>Read-only by default. When {@link OutstandingRequestsTableProps.activeMode}
 * is supplied, the chooser behaviour kicks in — only rows matching the mode are
 * clickable (provider rows additionally need a policy to target); the rest are
 * dimmed for context.
 */
export const OutstandingRequestsTable: React.FC<OutstandingRequestsTableProps> = ({
  requests,
  policyById,
  activeMode,
  onRowSelect,
  emptyMessage = 'No outstanding requests to nudge.',
  testId,
  rowTestIdPrefix,
}) => {
  const interactive = activeMode !== undefined;

  const handleClick = (request: ProfileRequestDTO, rowMode: NudgeMode) => {
    if (!interactive || !onRowSelect) return;
    // Only the entry-mode group is selectable; the other group is shown dimmed
    // for context and does nothing.
    if (rowMode !== activeMode) return;
    // Provider nudge needs a policy to target.
    if (rowMode === 'PROVIDER' && !request.policyId) return;
    onRowSelect(request, rowMode);
  };

  return (
    <div className="outstanding-requests" data-testid={testId}>
      <table className="outstanding-requests__table">
        <thead>
          <tr>
            <th>Provider</th>
            <th>Policy Type</th>
            <th>Policy No.</th>
            <th>Type</th>
            <th>Signatory</th>
            <th>Last updated</th>
            <th>Request type</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr>
              <td colSpan={8} className="outstanding-requests__empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            requests.map((request) => {
              const status = deriveDisplayStatus(request.status, request.actions);
              const rowMode = rowModeFor(request);
              const active = interactive && rowMode === activeMode;
              const policy = request.policyId ? policyById.get(request.policyId) : null;
              return (
                <tr
                  key={request.id}
                  className={
                    'outstanding-requests__row' +
                    (interactive
                      ? active
                        ? ' outstanding-requests__row--active'
                        : ' outstanding-requests__row--dimmed'
                      : '')
                  }
                  onClick={interactive ? () => handleClick(request, rowMode) : undefined}
                  data-testid={rowTestIdPrefix ? `${rowTestIdPrefix}${request.id}` : undefined}
                  data-active={interactive ? active : undefined}
                >
                  <td>
                    <ProviderCell request={request} policyById={policyById} />
                  </td>
                  <td>
                    <span className="policy-type">
                      {policy?.policyTypeName || policy?.category || request.requestProductName || '—'}
                    </span>
                  </td>
                  <td>
                    <span className="policy-number">{policy?.policyNumber || '—'}</span>
                  </td>
                  <td>{policy ? ownershipLabel(policy.ownershipType) : '—'}</td>
                  <td>{signatoryNamesFor(request, policy)}</td>
                  <td>
                    <span className="loa-created-date">
                      {formatLastUpdated(request.updatedAt ?? request.createdAt)}
                    </span>
                  </td>
                  <td>
                    <RequestTypeBadge type={request.requestType} />
                  </td>
                  <td>
                    <RequestDisplayStatusBadge status={status} />
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default OutstandingRequestsTable;
